export enum EvaluationLevel {
  STRONG = 'strong',
  MEDIUM = 'medium',
  WEAK = 'weak',
  TOO_SHORT = 'tooshort',
  TOO_LONG = 'toolong',
  INVALID = 'invalid',
}

export const DEFAULT_MIN_LENGTH = 6;
export const DEFAULT_MAX_LENGTH = 15;

export type EvaluationLevelConfig = Record<
  EvaluationLevel,
  {
    breakpoint: number;
  }
>;

export const evaluationLevels: EvaluationLevelConfig = {
  strong: {
    breakpoint: 9,
  },
  medium: {
    breakpoint: 7,
  },
  weak: {
    breakpoint: 1,
  },
  tooshort: {
    breakpoint: 0,
  },
  toolong: {
    breakpoint: -1,
  },
  invalid: {
    breakpoint: 0,
  },
};

/**
 * Evaluate the length of the password, the higher the length the higher the score
 * Invalid cases need to be handled before or otherwise
 * @param password is the password to be validated
 * @returns {number} equals a calculated score depending on the password length
 */
export const evaluatePasswordLength = (password: string): number => {
  let calculatedPasswordLengthScore = 0;
  if (password.length >= 6) {
    calculatedPasswordLengthScore += 1;
  }
  if (password.length >= 9) {
    calculatedPasswordLengthScore += 2;
  }
  if (password.length >= 12) {
    calculatedPasswordLengthScore += 1;
  }
  return calculatedPasswordLengthScore;
};

/**
 * Evaluate the usage of uppercase letters
 * @param password is the password to be validated
 * @returns {number} equals a calculated score depending on the usage of uppercase letters
 */
export const evaluateUpperCaseUsage = (password: string): number =>
  +(password.toLowerCase() !== password);

/**
 * Evaluate the usage of lowercase letters
 * @param password is the password to be validated
 * @returns {number} equals a calculated score depending on the usage of lowercase letters
 */
export const evaluateLowerCaseUsage = (password: string): number =>
  +(password.toUpperCase() !== password);

/**
 * Evaluate the usage of digits
 * @param password is the password to be validated
 * @returns {number} equals a calculated score depending on the usage of digits
 */
export const evaluateDigitsUsage = (password: string): number => {
  let calculatedPasswordDigitScore = 0;
  // Check for general usage of digits
  if (/\d/.test(password)) {
    calculatedPasswordDigitScore += 1;
  }
  // Check for usage of digits in the middle of the string
  if (/.+\d.+/.test(password)) {
    calculatedPasswordDigitScore += 1;
  }
  return calculatedPasswordDigitScore;
};

/**
 * Evaluate the usage of symbols
 * @param password is the password to be validated
 * @returns {number} equals a calculated score depending on the usage of symbols
 */
export const evaluateSymbolsUsage = (password: string): number => {
  let calculatedPasswordSymbolScore = 0;
  // Check for general usage of symbols
  if (/([^\d\w\s]|_)/.test(password)) {
    calculatedPasswordSymbolScore += 1;
  }
  // Check for usage of symbols in the middle of the string
  if (/.+([^\d\w\s]|_).+/.test(password)) {
    calculatedPasswordSymbolScore += 1;
  }
  return calculatedPasswordSymbolScore;
};

export const defaultEvaluationPipeline = [
  evaluatePasswordLength,
  evaluateUpperCaseUsage,
  evaluateLowerCaseUsage,
  evaluateDigitsUsage,
  evaluateSymbolsUsage,
];

/**
 * Evaluate a password on the basis of a scoring system dependent on different checks
 * @param password is the password to be validated
 * @param minLength is the minimum length of the password
 * @param maxLength is the maximum length of the password. Set this to -1 to disable
 * @returns {number} equals a calculated score depending on the different checks, ranges from 0 (invalid) to 10
 */
export const evaluatePassword = (
  password: string,
  minLength = DEFAULT_MIN_LENGTH,
  maxLength = DEFAULT_MAX_LENGTH,
  pipeline = defaultEvaluationPipeline,
): number => {
  if (password.length < minLength) {
    return 0;
  }
  if (maxLength > -1 && password.length > maxLength) {
    return -1;
  }
  return pipeline.reduce((score, step) => score + step(password), 0);
};

/**
 * Transforms the password score into one of the indication levels
 * @param passwordScore is the current score of the password as number
 * @param config the level config for the breakpoints
 * @returns the evaluation result
 */
export const getEvaluationLevelFromScore = (
  passwordScore: number,
  config: EvaluationLevelConfig = evaluationLevels,
): EvaluationLevel => {
  if (passwordScore >= config.strong.breakpoint) {
    return EvaluationLevel.STRONG;
  }
  if (passwordScore >= config.medium.breakpoint) {
    return EvaluationLevel.MEDIUM;
  }
  if (passwordScore >= config.weak.breakpoint) {
    return EvaluationLevel.WEAK;
  }
  if (passwordScore === 0) {
    return EvaluationLevel.TOO_SHORT;
  }
  if (passwordScore === -1) {
    return EvaluationLevel.TOO_LONG;
  }
  return EvaluationLevel.INVALID;
};

export const getEvaluationLevelFromPassword = (
  password: string,
  minLength = DEFAULT_MIN_LENGTH,
  maxLength = DEFAULT_MAX_LENGTH,
  config: EvaluationLevelConfig = evaluationLevels,
  pipeline = defaultEvaluationPipeline,
): EvaluationLevel => {
  const score = evaluatePassword(password, minLength, maxLength, pipeline);
  return getEvaluationLevelFromScore(score, config);
};
